import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle, Warning } from "@phosphor-icons/react";

export default function VendorOnboardPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [freshLinkSent, setFreshLinkSent] = useState(false);
  const [fallbackEmail, setFallbackEmail] = useState("");

  const requestFreshLink = async (useEmailFallback = false) => {
    if (requesting) return;
    if (useEmailFallback && !fallbackEmail.trim()) return;
    setRequesting(true);
    try {
      const body = useEmailFallback
        ? { email: fallbackEmail.trim() }
        : { token };
      await API.post("/suppliers/onboard/request-fresh-link", body);
      setFreshLinkSent(true);
    } catch {
      // Even on failure we show the neutral message so we don't leak existence info
      setFreshLinkSent(true);
    } finally { setRequesting(false); }
  };

  useEffect(() => {
    if (!token) {
      setError("No invitation token found. Please use the link from your email.");
      setLoading(false);
      return;
    }
    API.get(`/suppliers/onboard/${token}`)
      .then(({ data }) => setVendor(data))
      .catch((err) => setError(err.response?.data?.detail || "Invalid or expired invitation link."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (password.length < 8) { setFormError("Password must be at least 8 characters."); return; }
    if (password !== confirmPassword) { setFormError("Passwords do not match."); return; }

    setSubmitting(true);
    try {
      await API.post("/suppliers/accept-invite", { token, password });
      setSuccess(true);
    } catch (err) {
      setFormError(err.response?.data?.detail || "Failed to create account. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="vendor-onboard-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold font-[Chivo] tracking-tight">NECOS.FM</h1>
          <p className="text-sm text-muted-foreground mt-1">Supplier Portal Setup</p>
        </div>

        <div className="border border-border rounded-lg p-6 bg-card shadow-sm">
          {error ? (
            <div className="space-y-4" data-testid="onboard-error">
              <div className="text-center space-y-3">
                <Warning size={40} className="mx-auto text-destructive" weight="duotone" />
                <p className="text-sm text-destructive font-medium">{error}</p>
              </div>

              {freshLinkSent ? (
                <div className="border border-emerald-200 dark:border-emerald-900/50 rounded-md p-3 bg-emerald-50 dark:bg-emerald-900/20 text-xs text-center" data-testid="fresh-link-sent-msg">
                  <CheckCircle size={18} className="inline mr-1 text-emerald-500 mb-1" weight="fill" />
                  <p className="font-medium text-emerald-800 dark:text-emerald-300">Check your inbox</p>
                  <p className="text-emerald-700 dark:text-emerald-400 mt-1">
                    If we have a supplier record for you, a fresh invitation link has been emailed to your address on file. It's valid for 14 days.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {token && (
                    <Button variant="default" size="sm" className="w-full" onClick={() => requestFreshLink(false)} disabled={requesting} data-testid="request-fresh-link-btn">
                      {requesting ? "Sending…" : "Email me a new link"}
                    </Button>
                  )}

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                    <div className="relative flex justify-center text-[10px] uppercase tracking-wider">
                      <span className="bg-card px-2 text-muted-foreground">or recover with your email</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fallback-email" className="text-xs">Your supplier email address</Label>
                    <Input
                      id="fallback-email"
                      type="email"
                      placeholder="you@example.com"
                      value={fallbackEmail}
                      onChange={e => setFallbackEmail(e.target.value)}
                      data-testid="fallback-email-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => requestFreshLink(true)}
                      disabled={requesting || !fallbackEmail.trim()}
                      data-testid="request-by-email-btn"
                    >
                      {requesting ? "Sending…" : "Send me a new link"}
                    </Button>
                  </div>
                </div>
              )}

              <div className="text-center">
                <Link to="/login"><Button variant="ghost" size="sm" data-testid="onboard-go-login-btn">Already set up? Log in</Button></Link>
              </div>
            </div>
          ) : success ? (
            <div className="text-center space-y-4" data-testid="onboard-success">
              <CheckCircle size={40} className="mx-auto text-emerald-500" weight="duotone" />
              <h2 className="text-lg font-semibold font-[Chivo]">Account Created</h2>
              <p className="text-sm text-muted-foreground">Your account has been set up. You can now log in with your email and password.</p>
              <div className="border border-border rounded-md p-3 bg-muted/30 text-xs text-left space-y-1">
                <p><span className="font-semibold">Email:</span> {vendor?.email}</p>
                <p><span className="font-semibold">Company:</span> {vendor?.company_name}</p>
              </div>
              <Link to="/login"><Button className="w-full" data-testid="go-to-login-btn">Go to Login</Button></Link>
            </div>
          ) : (
            <>
              <div className="mb-6 space-y-2">
                <h2 className="text-lg font-semibold font-[Chivo]">Welcome, {vendor?.contact_name || vendor?.company_name}</h2>
                <p className="text-sm text-muted-foreground">Set a password to activate your supplier portal access.</p>
                <div className="border border-border rounded-md p-3 bg-muted/30 text-xs space-y-1">
                  <p><span className="font-semibold">Company:</span> {vendor?.company_name}</p>
                  <p><span className="font-semibold">Email:</span> {vendor?.email}</p>
                  {vendor?.trade && <p><span className="font-semibold">Trade:</span> {vendor?.trade}</p>}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="onboard-form">
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimum 8 characters" required data-testid="onboard-password" />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm Password *</Label>
                  <Input id="confirm" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm your password" required data-testid="onboard-confirm-password" />
                </div>
                {formError && <p className="text-sm text-destructive" data-testid="onboard-form-error">{formError}</p>}
                <Button type="submit" className="w-full" disabled={submitting} data-testid="onboard-submit-btn">
                  {submitting ? "Creating Account..." : "Create Account"}
                </Button>
              </form>

              <p className="text-center mt-4 text-xs text-muted-foreground">
                Already have an account? <Link to="/login" className="text-primary hover:underline">Log in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
